import '../AdminDetailOverview.scss';
import './RecruitSeoReview.scss';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

// 공고 정형 필드 검수 큐
// 자동 파싱 신뢰도가 낮은(LOW) 공고만 원본과 함께 보여주고, 관리자가 고친 값은 MANUAL 로 저장되어 자동 동기화가 덮어쓰지 않는다.

type Dict = Record<string, string>;
interface Codes {
  kinds: Dict;
  positions: Dict;
  depts: Dict;
  employmentTypes: Dict;
  payTypes: Dict;
  denominations: Dict;
  sido: Dict;
}

interface QueueItem {
  id: number;
  title?: string;
  church?: string;
  religiousbody?: string;
  location?: string;
  locationDetail?: string;
  address?: string;
  sort?: string;
  part?: string;
  partDetail?: string;
  pay?: string;
  date?: string;
  country?: string;
  city?: string;
  jobPosition?: string | null;
  jobDepts?: string | null;
  employmentType?: string | null;
  regionSido?: string | null;
  regionSigungu?: string | null;
  payType?: string | null;
  payMin?: number | null;
  payMax?: number | null;
  payNote?: string | null;
  denomination?: string | null;
  deadlineAt?: string | null;
  seoConfidence?: string | null;
  seoIssues?: string | null;
}

type Status = 'LOW' | 'MANUAL' | 'HIGH';
const STATUS_LABELS: Record<Status, string> = { LOW: '검수 필요', MANUAL: '검수 완료', HIGH: '자동 확정' };
const ISSUE_LABELS: Dict = { position: '직분', region: '지역', employment: '고용형태', pay: '사례비', deadline: '마감일(본문에서 읽음)' };

/** {sort, content} JSON 배열/일반 문자열 → 읽기용 문자열 */
function readable(raw?: string): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((e) => {
          if (!e || typeof e !== 'object') return String(e ?? '');
          const value = e.content ?? [e.paySort, e.selectCost, e.inputCost].filter(Boolean).join(' ');
          return parsed.length > 1 && e.sort ? `${e.sort}: ${value}` : value;
        })
        .filter(Boolean)
        .join(' / ');
    }
    if (parsed && typeof parsed === 'object') return Object.values(parsed).filter(Boolean).join(' ');
  } catch {
    // 일반 문자열
  }
  return raw;
}

const splitCodes = (v?: string | null) => (v ? v.split(',').filter(Boolean) : []);

function ReviewRow({ kind, item, codes, onSaved }: { kind: string; item: QueueItem; codes: Codes; onSaved: () => void }) {
  const [form, setForm] = useState({
    jobPosition: item.jobPosition || '',
    jobDepts: splitCodes(item.jobDepts),
    employmentType: splitCodes(item.employmentType),
    regionSido: item.regionSido || '',
    regionSigungu: item.regionSigungu || '',
    payType: item.payType || '',
    payMin: item.payMin ?? '',
    payMax: item.payMax ?? '',
    payNote: item.payNote || '',
    denomination: item.denomination || '',
    deadlineAt: (item.deadlineAt || '').slice(0, 10),
  });
  const [saving, setSaving] = useState(false);

  const set = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggle = (key: 'jobDepts' | 'employmentType', code: string) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(code) ? prev[key].filter((c) => c !== code) : [...prev[key], code],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${MainURL}/recruitseoadmin/save`, {
        kind,
        id: item.id,
        fields: { ...form, jobDepts: form.jobDepts.join(','), employmentType: form.employmentType.join(',') },
      });
      if (res.data?.success) onSaved();
      else alert(res.data?.message || '저장하지 못했습니다.');
    } catch {
      alert('저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('수동으로 고친 값을 버리고 자동 계산 값으로 되돌릴까요?')) return;
    const res = await axios.post(`${MainURL}/recruitseoadmin/reset`, { kind, id: item.id });
    if (res.data?.success) onSaved();
  };

  const showPayAmount = form.payType === 'MONTHLY' || form.payType === 'ANNUAL';
  const issues = splitCodes(item.seoIssues).map((i) => ISSUE_LABELS[i] || i);

  return (
    <div className="recruit-seo-review__row">
      <div className="recruit-seo-review__source">
        <div className="recruit-seo-review__source-head">
          <a href={`/recruit/${kind}/${item.id}`} target="_blank" rel="noopener noreferrer">
            #{item.id} {item.title}
          </a>
          {issues.length > 0 && <span className="recruit-seo-review__issues">확인 필요: {issues.join(', ')}</span>}
        </div>
        <dl>
          <dt>교회</dt><dd>{item.church} {item.religiousbody ? `(${item.religiousbody})` : ''}</dd>
          <dt>지역</dt><dd>{[item.location, item.locationDetail, item.country, item.city].filter(Boolean).join(' ')} {item.address ? `· ${item.address}` : ''}</dd>
          <dt>구분</dt><dd>{item.sort}</dd>
          <dt>파트</dt><dd>{readable(item.part)} {readable(item.partDetail) ? `/ ${readable(item.partDetail)}` : ''}</dd>
          <dt>사례비</dt><dd>{readable(item.pay)}</dd>
          <dt>등록일</dt><dd>{item.date}</dd>
        </dl>
      </div>

      <div className="recruit-seo-review__form">
        <label>
          <span>직분</span>
          <select value={form.jobPosition} onChange={(e) => set('jobPosition', e.target.value)}>
            <option value="">선택</option>
            {Object.entries(codes.positions).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </label>
        <div className="recruit-seo-review__field">
          <span>부서</span>
          <div className="recruit-seo-review__chips">
            {Object.entries(codes.depts).map(([code, label]) => (
              <button type="button" key={code} className={form.jobDepts.includes(code) ? 'is-on' : ''} onClick={() => toggle('jobDepts', code)}>{label}</button>
            ))}
          </div>
        </div>
        <div className="recruit-seo-review__field">
          <span>고용형태</span>
          <div className="recruit-seo-review__chips">
            {Object.entries(codes.employmentTypes).map(([code, label]) => (
              <button type="button" key={code} className={form.employmentType.includes(code) ? 'is-on' : ''} onClick={() => toggle('employmentType', code)}>{label}</button>
            ))}
          </div>
        </div>
        <label>
          <span>지역</span>
          <select value={form.regionSido} onChange={(e) => set('regionSido', e.target.value)}>
            <option value="">시도 선택</option>
            {Object.entries(codes.sido).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
          <input value={form.regionSigungu} placeholder="시군구 (예: 은평구)" onChange={(e) => set('regionSigungu', e.target.value)} />
        </label>
        <label>
          <span>사례비</span>
          <select value={form.payType} onChange={(e) => set('payType', e.target.value)}>
            <option value="">선택</option>
            {Object.entries(codes.payTypes).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
          {showPayAmount && (
            <>
              <input type="number" value={form.payMin} placeholder="최소(만원)" onChange={(e) => set('payMin', e.target.value)} />
              <input type="number" value={form.payMax} placeholder="최대(만원)" onChange={(e) => set('payMax', e.target.value)} />
            </>
          )}
        </label>
        <label>
          <span>사례비 비고</span>
          <input value={form.payNote} onChange={(e) => set('payNote', e.target.value)} />
        </label>
        <label>
          <span>교단</span>
          <select value={form.denomination} onChange={(e) => set('denomination', e.target.value)}>
            <option value="">선택</option>
            {Object.entries(codes.denominations).map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        </label>
        <label>
          <span>마감일</span>
          <input type="date" value={form.deadlineAt} onChange={(e) => set('deadlineAt', e.target.value)} />
          <small>비우면 채용 시 마감</small>
        </label>
        <div className="recruit-seo-review__actions">
          {item.seoConfidence === 'MANUAL' && (
            <button type="button" className="admin-inquiry-manage__toolbar-btn" onClick={reset}>자동값으로 되돌리기</button>
          )}
          <button type="button" className="admin-inquiry-manage__toolbar-btn admin-inquiry-manage__toolbar-btn--primary" disabled={saving} onClick={save}>
            {saving ? '저장 중...' : '검수 완료로 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecruitSeoReview() {
  const [codes, setCodes] = useState<Codes | null>(null);
  const [kind, setKind] = useState('minister');
  const [status, setStatus] = useState<Status>('LOW');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [counts, setCounts] = useState<Record<string, Record<string, number>>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${MainURL}/recruitseoadmin/codes`).then((res) => setCodes(res.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${MainURL}/recruitseoadmin/queue`, { params: { kind, status, page } });
      if (res.data?.success) {
        setItems(res.data.items);
        setTotal(res.data.total);
        setPageSize(res.data.pageSize);
        setCounts(res.data.counts);
      }
    } finally {
      setLoading(false);
    }
  }, [kind, status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const kindCounts = counts[kind] || {};
  const pending = Object.values(counts).reduce((sum, c) => sum + (c.PENDING || 0), 0);

  return (
    <div className="recruit-seo-review service-detail-overview">
      <p className="service-detail-overview__hint">
        공고의 직분·부서·지역·사례비를 자동으로 정형화한 결과 중 <b>신뢰도가 낮은 건만</b> 확인하는 화면입니다.
        저장하면 '검수 완료'가 되어 자동 동기화(10분 주기)가 값을 덮어쓰지 않습니다.
        {pending > 0 && ` 아직 동기화되지 않은 공고 ${pending}건은 다음 주기에 처리됩니다.`}
      </p>

      <div className="service-detail-overview__tabs">
        {codes && Object.entries(codes.kinds).map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={`service-detail-overview__tab${kind === key ? ' is-active' : ''}`}
            onClick={() => { setKind(key); setPage(1); }}
          >
            {label} {counts[key]?.LOW ? <span className="recruit-seo-review__badge">{counts[key].LOW}</span> : null}
          </button>
        ))}
      </div>

      <div className="recruit-seo-review__status">
        {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
          <button type="button" key={s} className={status === s ? 'is-active' : ''} onClick={() => { setStatus(s); setPage(1); }}>
            {STATUS_LABELS[s]} ({kindCounts[s] || 0})
          </button>
        ))}
      </div>

      {loading || !codes ? (
        <p className="service-detail-overview__empty">불러오는 중...</p>
      ) : items.length === 0 ? (
        <p className="service-detail-overview__empty">{status === 'LOW' ? '검수할 공고가 없습니다.' : '해당하는 공고가 없습니다.'}</p>
      ) : (
        <>
          {items.map((item) => (
            <ReviewRow key={`${kind}-${item.id}-${item.seoConfidence}`} kind={kind} item={item} codes={codes} onSaved={load} />
          ))}
          <div className="recruit-seo-review__pager">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
            <span>{page} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
          </div>
        </>
      )}
    </div>
  );
}
